import LegalLayout from "./LegalLayout";

// Verbatim migration of the current live Privacy Policy at
// https://zen-sips.preview.emergentagent.com/api/privacy
// Content owner: Progressive Synergy Sdn Bhd (Matcha Story).
export default function PrivacyPolicy() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Privacy Policy"
      subtitle="Last updated: 22 September 2026"
    >
      <p>
        This Privacy Policy explains how <strong>Progressive Synergy Sdn Bhd</strong>
        (&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo;),
        operator of the Matcha Story mobile application and related services
        (the &ldquo;Application&rdquo; or &ldquo;Service&rdquo;), collects, uses,
        discloses and protects your personal data.
      </p>
      <p>
        By using the Application, you consent to the practices described in this
        Privacy Policy. We process personal data in accordance with the
        Malaysian Personal Data Protection Act 2010 (PDPA) and other applicable
        laws.
      </p>
      <p>
        If you do not agree with this Privacy Policy, please do not use the
        Application.
      </p>

      <Section n="1" title="Information We Collect">
        <p>We may collect the following categories of personal data when you use the Matcha Story app:</p>
        <ul>
          <li><strong>Account information:</strong> your name or username, mobile phone number, email address (if provided), and account ID used to create and manage your account.</li>
          <li><strong>Optional profile details:</strong> gender, race and date of birth, where you choose to provide them.</li>
          <li><strong>Order and transaction data:</strong> items ordered, order history, in-store purchases, pickup or delivery preferences, and order notes.</li>
          <li><strong>Loyalty data:</strong> points, stamps, membership tier, vouchers, rewards and related membership activity.</li>
          <li><strong>Wallet and payment-related data:</strong> wallet balance, top-up records and transaction status. Payments are processed through our third-party payment provider. We do not collect or store full payment card or banking details unless expressly stated.</li>
          <li><strong>Communications:</strong> messages you send to our store through the in-app chat and any support enquiries you submit.</li>
          <li><strong>Device and usage data:</strong> basic technical information, such as app version, device or diagnostic information, and app usage events, where collected to operate, maintain and improve the app.</li>
        </ul>
        <p>We collect and use personal data only as necessary for the purposes described in this Privacy Policy and in accordance with applicable data protection laws. The information collected may vary depending on the features you use and the information you choose to provide.</p>
      </Section>

      <Section n="2" title="How We Use Your Information">
        <p>We use your personal data to:</p>
        <ul>
          <li>create and manage your account and verify your identity;</li>
          <li>process and fulfil your orders, pickups and deliveries;</li>
          <li>operate our loyalty programme, points, stamps, vouchers and rewards;</li>
          <li>process payments and top-ups, and maintain your wallet balance;</li>
          <li>respond to your chat messages, enquiries and support requests;</li>
          <li>send you service-related notifications and, where permitted, promotional messages;</li>
          <li>detect, prevent and address fraud, abuse, security issues and technical problems;</li>
          <li>improve and maintain the Application and Service;</li>
          <li>comply with legal, regulatory and tax obligations.</li>
        </ul>
      </Section>

      <Section n="3" title="Payment Processing">
        <p>Matcha Story uses <strong>Razorpay</strong> as a payment service provider to facilitate payments. Payment card and payment method details are processed by Razorpay in accordance with its privacy policy.</p>
        <p>Matcha Story does not store full payment card numbers on its own systems. Depending on the transaction, we may receive and retain transaction-related information such as payment status, transaction reference, amount and order details to confirm purchases, manage orders, maintain loyalty records and address customer enquiries.</p>
      </Section>

      <Section n="4" title="How We Share Your Information">
        <p>We do not sell your personal data. We may share personal data with:</p>
        <ul>
          <li>payment service providers, to process transactions;</li>
          <li>cloud hosting and storage providers, to operate the Application and store uploaded content securely;</li>
          <li>delivery or logistics partners, where you request delivery;</li>
          <li>professional advisers, auditors and regulators, where required;</li>
          <li>government or law-enforcement authorities, where required by law or to protect our rights;</li>
          <li>a successor entity, in the event of a merger, acquisition or restructuring, subject to applicable law.</li>
        </ul>
        <p>We require our service providers to protect your personal data and use it only for the purposes we specify.</p>
      </Section>

      <Section n="5" title="Data Storage and Security">
        <p>We take reasonable technical and organisational measures to protect your personal data against unauthorised access, loss, misuse or alteration.</p>
        <p>Passwords are stored using industry-standard one-way hashing and are never stored in plain text.</p>
        <p>Uploaded images and files are stored using a managed cloud object-storage service.</p>
        <p>However, no method of electronic storage or transmission is completely secure, and we cannot guarantee absolute security.</p>
      </Section>

      <Section n="6" title="Data Retention">
        <p>We retain your personal data for as long as your account is active or as needed to provide the Service, and thereafter only as required to comply with legal, accounting, tax or regulatory obligations, resolve disputes and enforce our agreements.</p>
        <p>When personal data is no longer required, we will take reasonable steps to delete or anonymise it.</p>
      </Section>

      <Section n="7" title="Your Rights and Choices">
        <p>Subject to applicable law, you may:</p>
        <ul>
          <li>access and review the personal data associated with your account;</li>
          <li>update or correct your profile information within the Application;</li>
          <li>withdraw consent to marketing communications through the app&apos;s notification settings or by contacting us;</li>
          <li>request deletion of your account and associated personal data.</li>
        </ul>
        <p>
          You may permanently delete your account through our self-service page at{" "}
          <a href="/delete-account" className="text-matcha-dark underline underline-offset-4 hover:text-ink">
            https://matchastory.my/delete-account
          </a>
          {" "}or through the in-app account settings, where available. When you submit an account deletion request, we will process it and permanently delete your account and associated personal data, except for information we are required or permitted to retain under applicable law. Any retained information will be handled in accordance with this Privacy Policy and applicable legal requirements.
        </p>
        <p>
          You may also contact us using the details in the &ldquo;Contact Us&rdquo; section to exercise your privacy rights or ask questions about your personal data.
        </p>
      </Section>

      <Section n="8" title="Account and Data Deletion">
        <p>When you delete your account, we will delete or anonymise your profile, order history, loyalty balances, vouchers and chat messages associated with your account, except where we are required or permitted by law to retain certain records (for example, transaction records for tax or accounting purposes).</p>
        <p>Some information may remain in routine backups for a limited period before being overwritten.</p>
      </Section>

      <Section n="9" title="Marketing Communications">
        <p>Where you have consented, we may send you promotional messages about products, offers and rewards.</p>
        <p>You can opt out of promotional notifications at any time through the notification settings in the Application, without affecting service-related messages that are necessary to operate your account.</p>
      </Section>

      <Section n="10" title="Children">
        <p>The Application is intended for users who meet the minimum age required under applicable law.</p>
        <p>We do not knowingly collect personal data from children without appropriate consent. If you believe a child has provided us with personal data without consent, please contact us so we can take appropriate action.</p>
      </Section>

      <Section n="11" title="Third-Party Services">
        <p>The Application may link to or integrate with third-party services, such as payment providers, delivery providers and app stores.</p>
        <p>Those third parties operate under their own privacy policies, and we are not responsible for their practices. We encourage you to review their policies.</p>
      </Section>

      <Section n="12" title="International Data Transfers">
        <p>Your personal data may be processed or stored on servers located outside Malaysia by our service providers.</p>
        <p>Where personal data is transferred outside Malaysia, we take reasonable steps to ensure it is protected consistently with this Privacy Policy and applicable law.</p>
      </Section>

      <Section n="13" title="Changes to this Privacy Policy">
        <p>We may update this Privacy Policy from time to time.</p>
        <p>Where appropriate, we will notify you of material changes through the Application or other reasonable means.</p>
        <p>Your continued use of the Application after the updated Privacy Policy takes effect constitutes acceptance of the changes, to the extent permitted by law.</p>
      </Section>

      <Section n="14" title="Advertising and Tracking">
        <p>Matcha Story does not use your personal data to track you across other companies&apos; apps or websites for targeted advertising. We do not sell your personal data to third parties for advertising purposes.</p>
        <p>We may send you updates about Matcha Story, including promotions, rewards, offers and membership-related announcements. You may manage your marketing communication preferences through the app&apos;s notification settings or by contacting us.</p>
        <p>If our advertising or tracking practices change, we will update this Privacy Policy and obtain any consent required by applicable law.</p>
      </Section>

      <Section n="15" title="Contact Us">
        <p>If you have any questions, requests or complaints regarding this Privacy Policy or your personal data, please contact:</p>
        <p>
          <strong>Progressive Synergy Sdn Bhd</strong><br />
          Brand: Matcha Story<br />
          Email: <a href="mailto:hello@matchastory.my" className="text-matcha-dark underline underline-offset-4">hello@matchastory.my</a>
        </p>
        <p>We will make reasonable efforts to respond to legitimate requests within a reasonable time and in accordance with applicable law.</p>
      </Section>
    </LegalLayout>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="pt-4" data-testid={`privacy-section-${n}`}>
      <h2 className="overline text-ink mb-3">
        <span className="text-matcha-dark mr-2">{n}.</span>
        {title}
      </h2>
      <div className="space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5">
        {children}
      </div>
    </section>
  );
}
